import {Link, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {useState} from 'react';
import {LoginPayload, useLogin} from '../requests/use-login';
import {useNavigate} from '../../utils/hooks/use-navigate';
import {useAuth} from '../use-auth';
import {useSettings} from '../../core/settings/use-settings';
import {Trans} from '../../i18n/trans';
import {useTrans} from '../../i18n/use-trans';
import {StaticPageTitle} from '../../seo/static-page-title';

interface Props {
  onTwoFactorChallenge: () => void;
}

export function LoginPage({onTwoFactorChallenge}: Props) {
  const {trans} = useTrans();
  const [searchParams] = useSearchParams();
  const searchParamsEmail = searchParams.get('email') || '';
  const navigate = useNavigate();
  const {getRedirectUri} = useAuth();
  const {branding} = useSettings();
  const logoSrc = branding?.logo_dark || branding?.logo_light || 'images/logo-dark.png';

  const [step, setStep] = useState<1 | 2>(searchParamsEmail ? 2 : 1);

  const form = useForm<LoginPayload>({
    defaultValues: {
      remember: true,
      email: searchParamsEmail,
      password: '',
    },
  });

  const login = useLogin(form);

  const emailValue = form.watch('email');

  const handleNext = () => {
    if (!emailValue || !emailValue.trim()) {
      form.setError('email', {message: 'Vui lòng nhập tên tài khoản hoặc email'});
      return;
    }
    form.clearErrors('email');
    setStep(2);
  };

  const rawErrorMessage =
    form.formState.errors.email?.message ||
    form.formState.errors.password?.message ||
    (login.error as any)?.response?.data?.message ||
    (login.error as any)?.message;

  const errorMessage = rawErrorMessage
    ? trans({message: rawErrorMessage})
    : null;

  return (
    <div
      className="min-h-screen w-full flex flex-col justify-center items-center relative box-border bg-[#f3f3f3] text-[#1b1b1b]"
      style={{
        backgroundImage:
          "url('https://aadcdn.msauth.net/shared/1.0/content/images/backgrounds/2_bc3d32a696895f78c19df6c717586a5d.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: "'Segoe UI', 'Helvetica Neue', 'Lucida Grande', 'Roboto', sans-serif",
      }}
    >
      <StaticPageTitle>
        <Trans message="Đăng nhập - TDMU.DEV - URL Shortener" />
      </StaticPageTitle>

      {/* Main Container */}
      <div className="w-full max-w-[440px] p-20 flex flex-col box-border">
        {/* Main Sign-in Card */}
        <div className="bg-white p-36 sm:p-44 shadow-[0_4px_16px_rgba(0,0,0,0.12)] border border-[#e5e5e5] rounded-[4px] w-full box-border">
          {/* TDMU.DEV Logo & System Name */}
          <div className="mb-8">
            <Link to="/" aria-label="TDMU.DEV" className="inline-block">
              <img
                src={logoSrc}
                className="h-32 max-h-36 w-auto block object-contain"
                alt="TDMU.DEV"
              />
            </Link>
          </div>

          <div className="text-[15px] font-semibold text-[#1b1b1b] mb-24 tracking-[0.5px]">
            TDMU.DEV - URL Shortener
          </div>

          {errorMessage && (
            <div className="text-[#e81123] text-[14px] mb-16 leading-tight bg-[#fde7e9] p-10 rounded border border-[#f3b2b8]">
              {errorMessage}
            </div>
          )}

          <form
            onSubmit={form.handleSubmit(payload => {
              login.mutate(payload, {
                onSuccess: response => {
                  if (response.two_factor) {
                    onTwoFactorChallenge();
                  }
                },
              });
            })}
          >
            {step === 1 ? (
              /* Step 1: Username / Email */
              <div>
                <h1 className="text-[24px] font-semibold text-[#1b1b1b] leading-[28px] mb-20">
                  Đăng nhập
                </h1>

                <div className="mb-24">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Nhập tên tài khoản hoặc email"
                      autoFocus
                      className="w-full px-12 py-10 text-[15px] border border-[#8a8886] hover:border-[#323130] focus:border-[#0067b8] focus:ring-1 focus:ring-[#0067b8] outline-none bg-[#fdfdfd] focus:bg-white text-[#1b1b1b] placeholder-[#666] transition-all rounded-[2px] box-border shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)]"
                      {...form.register('email', {required: true})}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleNext();
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="flex justify-end mt-28">
                  <button
                    type="button"
                    onClick={handleNext}
                    className="bg-[#0067b8] hover:bg-[#005da6] active:bg-[#004d8c] text-white px-32 py-8 text-[15px] font-normal min-w-[108px] transition-colors cursor-pointer border-none shadow-none rounded-[2px]"
                  >
                    Tiếp theo
                  </button>
                </div>
              </div>
            ) : (
              /* Step 2: Password */
              <div>
                <div className="text-[15px] mb-16 flex items-center gap-6">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="p-4 -ml-4 rounded-full hover:bg-[#f2f2f2] text-[#1b1b1b] border-none bg-transparent cursor-pointer flex items-center justify-center transition-colors"
                    title="Quay lại"
                  >
                    <svg className="w-20 h-20 fill-current" viewBox="0 0 24 24">
                      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                    </svg>
                  </button>
                  <span className="font-normal text-[#1b1b1b] truncate max-w-[280px]">
                    {emailValue}
                  </span>
                </div>

                <h1 className="text-[24px] font-semibold text-[#1b1b1b] leading-[28px] mb-20">
                  Nhập mật khẩu
                </h1>

                <div className="mb-16">
                  <input
                    type="password"
                    placeholder="Mật khẩu"
                    autoFocus
                    className="w-full px-12 py-10 text-[15px] border border-[#8a8886] hover:border-[#323130] focus:border-[#0067b8] focus:ring-1 focus:ring-[#0067b8] outline-none bg-[#fdfdfd] focus:bg-white text-[#1b1b1b] placeholder-[#666] transition-all rounded-[2px] box-border shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)]"
                    {...form.register('password', {required: true})}
                  />
                </div>

                <div className="text-[13px] mb-16">
                  <Link to="/forgot-password" className="text-[#0067b8] hover:underline block mb-12">
                    Quên mật khẩu?
                  </Link>
                </div>

                {/* Stay signed in for a month */}
                <div className="mb-24">
                  <label className="flex items-center gap-8 cursor-pointer select-none text-[13px] text-[#1b1b1b]">
                    <input
                      type="checkbox"
                      className="w-16 h-16 rounded border-slate-400 text-[#0067b8] focus:ring-[#0067b8] cursor-pointer"
                      {...form.register('remember')}
                    />
                    <span>Duy trì đăng nhập trong 30 ngày</span>
                  </label>
                </div>

                <div className="flex justify-end mt-24">
                  <button
                    type="submit"
                    disabled={login.isPending}
                    className="bg-[#0067b8] hover:bg-[#005da6] active:bg-[#004d8c] text-white px-32 py-8 text-[15px] font-normal min-w-[108px] transition-colors cursor-pointer border-none shadow-none disabled:opacity-60 rounded-[2px]"
                  >
                    {login.isPending ? 'Đang đăng nhập...' : 'Đăng nhập'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
